/**
 * How long a BLIND CORNER unit is.
 *
 * A blind corner runs one leg to the vertex and stops the other a corner-box
 * short, so the box has the neighbour's SIDE across its front and only the
 * remainder of the unit carries a door:
 *
 *     door = length − neighbour's depth
 *
 * Against a standard 24" neighbour a 36" unit shows a 12" leaf and a 48" one
 * shows 24". That difference is the whole reason a size is CHOSEN rather than
 * fixed — a 12" leaf is the barely-reachable opening the 2026-09-10 corner
 * audit was about.
 *
 * This module is the rule and the size sets; it deliberately knows nothing
 * about runs, families or walls. `cabinet-runs/families.js` keys the sets to
 * the three product families, and the generator (which has no family) uses the
 * DEFAULT set. It lives in `kitchen-assembly` because `cabinet-runs` imports
 * from here and never the reverse — putting it the other way round would make
 * the generator depend on the manual-run engine.
 *
 * Inches throughout, except where a leg is compared against a size (`pickCornerLIn`,
 * `shortestCornerBlindM`), which is done in metres. Pure.
 */

const { INCHES_TO_METERS } = require('./constants');

/** The shortest unit that leaves any door at all beside a standard neighbour. */
const CORNER_BLIND_MIN_IN = 36;
/** The widest blind base made. */
const CORNER_BLIND_MAX_IN = 48;
/**
 * Leftover smaller than this is not a cabinet, so it stands as a filler.
 * 9" = `MIN_STANDALONE_CABINET_WIDTH` / `MIN_MODULE_IN`, the narrowest box sold.
 */
const MIN_CABINET_WIDTH_IN = 9;
const EPS_IN = 1e-6;

/**
 * The three size sets, on the same axis the module fill splits on: a family
 * differs in WHICH lengths it sells, and nothing else.
 *
 *   continuous  any length in range — the unit stretches, no filler
 *   grid        36-48 in 3" steps — a filler takes up to 3"
 *   stock       36 or 48 — a filler makes up the rest
 */
const CORNER_BLIND_SETS = {
  continuous: { continuous: true, sizes: [48, 36] },
  grid: { continuous: false, sizes: [48, 45, 42, 39, 36] },
  stock: { continuous: false, sizes: [48, 36] },
  // Venus Cabinetry's blind base corners: BBC39/42 and BBC42/45, each
  // adjustable across its pair — so 39, 42 or 45 inches of leg.
  venus: { continuous: false, sizes: [45, 42, 39] },
};

/**
 * The set a run with no family draws from. Matches `DEFAULT_FAMILY_ID`
 * (Semi-Custom) so the generator and an unset manual run answer alike.
 */
const DEFAULT_CORNER_BLIND_SET = CORNER_BLIND_SETS.grid;

/**
 * The unit length for a leg with `freeIn` inches of clear run, or 0 when the
 * leg cannot host one.
 *
 * WIDEST-FIRST WITH A CLEAN REMAINDER: take the longest size whose leftover is
 * either nothing or itself a real cabinet. An orphan filler at a corner is what
 * the user rule forbids ("a corner is a corner unit or combined with the
 * adjacent slot, never a counter break"), so a 55" leg takes 36" + a 19" box
 * rather than 48" + a 7" sliver.
 *
 * When no size leaves a clean remainder the sets part company, and that is the
 * point of having them: a continuous set stretches to swallow the leftover
 * (42.4" of leg is a 42.4" unit), a discrete one takes the widest that fits and
 * lets its own filler cover the rest — which is what that family already does
 * on every straight span.
 */
function pickCornerBlindIn(set, freeIn) {
  const { sizes = [], continuous = false } = set || DEFAULT_CORNER_BLIND_SET;
  const min = sizes.length ? Math.min(...sizes) : CORNER_BLIND_MIN_IN;
  if (!(freeIn >= min - EPS_IN)) return 0;

  // A CONTINUOUS family builds any width, so it never picks a rung: it takes
  // the whole leg, capped at the widest blind base. The only adjustment is when
  // that would strand a leftover too narrow to be a cabinet — then it gives the
  // leftover exactly a minimum box and keeps the rest, which is still the
  // widest door available without an orphan filler.
  //
  // Rung-picking here would be a real loss: 47.9" of leg is one 47.9" unit with
  // a 24" door, not a 36" unit with a 12" door beside an 11.9" box.
  if (continuous) {
    const capped = Math.min(freeIn, CORNER_BLIND_MAX_IN);
    const rest = freeIn - capped;
    if (rest <= EPS_IN || rest >= MIN_CABINET_WIDTH_IN - EPS_IN) return capped;
    return Math.max(min, freeIn - MIN_CABINET_WIDTH_IN);
  }

  const fits = sizes
    .filter((size) => size <= freeIn + EPS_IN)
    .sort((a, b) => b - a);
  const clean = fits.find((size) => {
    const rest = freeIn - size;
    return rest <= EPS_IN || rest >= MIN_CABINET_WIDTH_IN - EPS_IN;
  });
  if (clean != null) return clean;
  return fits.length ? fits[0] : 0;
}

// ---------------------------------------------------------------------------
// The UPPER tier's blind corner: a blind corner WALL cabinet
// ---------------------------------------------------------------------------

/** A wall cabinet's 12" buried behind its neighbour, and a 12" door. */
const UPPER_CORNER_BLIND_MIN_IN = 24;
/** The widest wall cabinet made (`MAX_MODULE_IN`). */
const UPPER_CORNER_BLIND_MAX_IN = 36;

/**
 * The same three families, the same axis — WHICH widths are sold:
 *
 *   continuous  any width in range
 *   grid        24-36 in 3" steps
 *   stock       24, 30 or 36
 */
const UPPER_CORNER_BLIND_SETS = {
  continuous: { continuous: true, sizes: [24, 36] },
  grid: { continuous: false, sizes: [24, 27, 30, 33, 36] },
  stock: { continuous: false, sizes: [24, 30, 36] },
  // Venus Cabinetry's blind wall corner is 27" (WBC27xx).
  venus: { continuous: false, sizes: [27] },
};
const DEFAULT_UPPER_CORNER_BLIND_SET = UPPER_CORNER_BLIND_SETS.grid;

/**
 * The blind wall cabinet's width for `freeIn` inches of wall between the
 * vertex and the first seam the uppers must keep, or 0 when none fits.
 *
 * NARROWEST-FIRST WITH A CLEAN REMAINDER — the opposite way round from the
 * base unit above, on purpose. A blind BASE is chosen wide because its door is
 * the only way into a 24"-deep box: 36" of unit is a 12" leaf you can barely
 * reach through. A blind WALL cabinet buries 12" of a 12"-deep box, so the 24"
 * standard already gives a 12" door onto all of it, and the wall beside it is
 * better spent on an ordinary cabinet with a full-width door (user ruling
 * 2026-09-21: "for uppers, blind cabinets should prob only be 24x12 instead of
 * … 36x12 — or we could provide a range of sizes, similar to the lowers"). So
 * 36" of wall is a 24" unit and a 12" cabinet, which is also exactly what
 * stands beside a 24" upper L.
 *
 * It grows only when the leftover would be too narrow to be a cabinet: 30" of
 * wall is a 30" unit, not 24" and a 6" sliver. Where no size leaves a clean
 * remainder the sets part company as they do for the base — a continuous
 * family stretches to take the whole wall, a discrete one takes the widest
 * that fits and its own filler covers the rest.
 */
function pickUpperCornerBlindIn(set, freeIn) {
  const { sizes = [], continuous = false } =
    set || DEFAULT_UPPER_CORNER_BLIND_SET;
  const min = sizes.length ? Math.min(...sizes) : UPPER_CORNER_BLIND_MIN_IN;
  if (!(freeIn >= min - EPS_IN)) return 0;
  const clean = (size) => {
    const rest = freeIn - size;
    return rest <= EPS_IN || rest >= MIN_CABINET_WIDTH_IN - EPS_IN;
  };
  if (continuous) {
    if (clean(min)) return min;
    // The leftover is a sliver: take it in, up to the widest cabinet made, and
    // past that leave it a real box.
    return freeIn <= UPPER_CORNER_BLIND_MAX_IN + EPS_IN
      ? freeIn
      : Math.min(UPPER_CORNER_BLIND_MAX_IN, freeIn - MIN_CABINET_WIDTH_IN);
  }
  const fits = sizes
    .filter((size) => size <= freeIn + EPS_IN)
    .sort((a, b) => a - b);
  const narrowestClean = fits.find(clean);
  if (narrowestClean != null) return narrowestClean;
  return fits.length ? fits[fits.length - 1] : 0;
}

// ---------------------------------------------------------------------------
// The L corner unit (a lazy susan): both legs, one size
// ---------------------------------------------------------------------------

/**
 * The L corner unit's leg, measured from the vertex along EACH wall: the 24"
 * corner box plus a leaf. Every family sells the 36" (`CORNER_CABINET_FOOTPRINT`);
 * Venus Cabinetry also sells a 33" (LSB33 beside LSB36), which is the L a
 * corner gets when a leg has 33–36" clear. Without it that corner fell to a
 * blind unit, and in Venus — whose blind bases start at 39" — often to nothing
 * at all (Golden West, 2026-10-19: a 34.6" and a 38.5" leg).
 *
 * Keyed by the same family key as the blind sets: one family is one way of
 * selling corner units, whichever unit is asked for. The generic families
 * keep the one 36" they have always built.
 */
const CORNER_L_SETS = {
  continuous: { sizes: [36] },
  grid: { sizes: [36] },
  stock: { sizes: [36] },
  venus: { sizes: [36, 33] },
};
const DEFAULT_CORNER_L_SET = CORNER_L_SETS.grid;

/**
 * How far under a unit's length a leg may measure and still take it, in
 * metres: float noise, not a design tolerance. An appliance seated flush
 * against a corner reservation leaves a leg of exactly the reservation's
 * length, which the arithmetic lands a hair either side of — and a strict
 * `>=` let that hair decide, so 787 such corpus corners got an L and ~210 did
 * not (2026-10-21; user ruling: a leg of exactly the size takes it). The run
 * engine already built the L on every one of them.
 */
const LEG_FIT_EPS = 1e-6;

/**
 * Whether a corner leg clear for `clearM` takes a unit `needM` long — an L's
 * leg, a blind base, the corner box. ONE comparison for the generator's
 * classifier and the run engine's plan and grading, so the two cannot split
 * on the noise either side of an exact fit.
 */
function legTakes(clearM, needM) {
  return clearM >= needM - LEG_FIT_EPS;
}

/** The widest L the set sells that both legs can take, in INCHES, or 0. */
function pickCornerLIn(set, freeAM, freeBM) {
  const { sizes = [] } = set || DEFAULT_CORNER_L_SET;
  const fit = [...sizes]
    .sort((a, b) => b - a)
    .find((size) => {
      const leg = size * INCHES_TO_METERS;
      return legTakes(freeAM, leg) && legTakes(freeBM, leg);
    });
  return fit ?? 0;
}

function shortestCornerBlindM(set) {
  const { sizes = [] } = set || DEFAULT_CORNER_BLIND_SET;
  const min = sizes.length ? Math.min(...sizes) : CORNER_BLIND_MIN_IN;
  return min * INCHES_TO_METERS;
}

// ---------------------------------------------------------------------------
// A size the USER picks, either tier
// ---------------------------------------------------------------------------

/**
 * Every size a family sells for a blind unit, and which of them fit `freeIn`
 * inches of leg — the list a person chooses from, beside the rule above that
 * chooses for them. A DISCRETE family is its rungs, each graded; a CONTINUOUS
 * one sells any length between its rungs, so it reports the range that fits
 * instead (`max` is the leg's, capped at the widest made; `feasible` says the
 * range is not empty).
 *
 * Fit is the whole grade. The clean-remainder rule is the automatic pick's —
 * a person choosing 48" of a 55" leg is choosing the 7" filler beside it.
 */
function blindSizeChoices(set, freeIn) {
  const { sizes = [], continuous = false } = set;
  const lo = Math.min(...sizes);
  const hi = Math.max(...sizes);
  if (continuous) {
    const max = Math.min(hi, freeIn);
    return { continuous: true, min: lo, max, feasible: max >= lo - EPS_IN };
  }
  return {
    continuous: false,
    sizes: [...sizes]
      .sort((a, b) => a - b)
      .map((size) => ({ size, feasible: size <= freeIn + EPS_IN })),
  };
}

/** Is `sizeIn` one the family sells that fits `freeIn` inches of leg? */
function fitsBlindSize(set, sizeIn, freeIn) {
  if (!(sizeIn > 0)) return false;
  const c = blindSizeChoices(set, freeIn);
  if (c.continuous)
    return c.feasible && sizeIn >= c.min - EPS_IN && sizeIn <= c.max + EPS_IN;
  return c.sizes.some((o) => o.feasible && Math.abs(o.size - sizeIn) <= EPS_IN);
}

module.exports = {
  CORNER_L_SETS,
  DEFAULT_CORNER_L_SET,
  legTakes,
  pickCornerLIn,
  shortestCornerBlindM,
  blindSizeChoices,
  fitsBlindSize,
  UPPER_CORNER_BLIND_SETS,
  DEFAULT_UPPER_CORNER_BLIND_SET,
  UPPER_CORNER_BLIND_MIN_IN,
  UPPER_CORNER_BLIND_MAX_IN,
  pickUpperCornerBlindIn,
  CORNER_BLIND_SETS,
  DEFAULT_CORNER_BLIND_SET,
  CORNER_BLIND_MIN_IN,
  CORNER_BLIND_MAX_IN,
  pickCornerBlindIn,
};
